/**
 * « Sessions TCF Canada & examens urgents »: the AI searches the web for the subjects of past
 * TCF Canada sessions reported online, then designs an ORIGINAL urgent mock exam with the official
 * structure (every week at night, or on demand here). The admin reads it and sends it by hand.
 * The learners do nothing: they only choose their exam centre.
 */

import { useAcademyFormatRetrieve } from "@agencyos/api-client";
import { GlassCard, SelectField, TextField } from "@agencyos/ui-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { centersOf, currentMonth, type ExamCenter } from "./academyData";
import { useAdminExam, useExamTexts } from "./examContext";
import { SessionExam } from "./SessionExam";

export function useExamCenters(): ExamCenter[] {
  const { params } = useAdminExam();
  const format = useAcademyFormatRetrieve(params);
  return format.data?.status === 200 ? centersOf(format.data.data.centers) : [];
}

export function centerOptions(centers: ExamCenter[], allLabel: string) {
  // D-123: the session packs are per city; two centres of one city (British Council and IDP in
  // Yaoundé for the IELTS) gave two options with the same value: one option per city now
  const byCity = new Map<string, string[]>();
  for (const c of centers) byCity.set(c.city, [...(byCity.get(c.city) ?? []), c.name]);
  return [{ value: "", label: allLabel }, ...[...byCity].map(([city, names]) => ({ value: city, label: names.length > 1 ? `${city} (${names.join(", ")})` : (names[0] ?? city) }))];
}

export function SessionsTab({ onOpenMocks }: { onOpenMocks?: () => void }) {
  const { t } = useTranslation();
  const { ielts, exam } = useAdminExam();
  const texts = useExamTexts();
  const centers = useExamCenters();
  const [month, setMonth] = useState(currentMonth);
  const [center, setCenter] = useState("");
  return (
    <GlassCard as="section" className="stack" aria-labelledby="sessions-title">
      <div className="section-title">
        <div>
          <h2 id="sessions-title">{texts.title("sessions", "academyAdmin.sessions.title")}</h2>
          <p className="muted">
            {ielts ? t("academyAdmin.ielts.sessions.intro") : exam === "tcf_canada" ? t("academyAdmin.sessions.intro") : t("academyAdmin.sessions.introExam", { exam: texts.examName })}
          </p>
        </div>
      </div>
      <p role="note" className="notice-warning installment-row">
        {t("academyAdmin.sessions.originalOnly")}
      </p>
      <div className="form-grid academy-filters">
        <TextField
          type="month"
          label={t("academyAdmin.analysis.month")}
          hint={t("academyAdmin.analysis.monthHint")}
          placeholder="2026-09"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
        <SelectField
          label={t("academyAdmin.sessions.center")}
          hint={t("academyAdmin.sessions.centerHint")}
          value={center}
          options={centerOptions(centers, t("academyAdmin.sessions.allCenters"))}
          onChange={(e) => setCenter(e.target.value)}
        />
      </div>
      <SessionExam
        month={month}
        center={center}
        onOpenMocks={onOpenMocks}
        onSelect={(nextMonth, nextCenter) => {
          setMonth(nextMonth);
          setCenter(nextCenter);
        }}
      />
    </GlassCard>
  );
}
